import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { getMediaEmbedUrl } from '@/lib/mediaUtils'
import { trackMediaEvent } from '@/lib/analytics/mediaEvents'
import type { MediaVideo } from '@/types/media'

interface MediaVideoModalProps {
  video: MediaVideo | null
  onClose: () => void
}

export function MediaVideoModal({ video, onClose }: MediaVideoModalProps) {
  useEffect(() => {
    if (!video) return
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [video, onClose])

  if (!video || typeof document === 'undefined') return null

  const embedUrl = getMediaEmbedUrl(video, true)

  return createPortal(
    <AnimatePresence>
      {video ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-neutral-950 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
              aria-label="Close video"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="aspect-video w-full bg-black">
              {embedUrl ? (
                <iframe
                  title={video.title}
                  src={embedUrl}
                  className="h-full w-full border-0"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                  onLoad={() =>
                    trackMediaEvent('media_video_play', {
                      video_id: video.id,
                      video_slug: video.slug,
                      platform: video.platform,
                      mode: 'modal',
                    })
                  }
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-neutral-400">
                  Unable to load embed.{' '}
                  <a href={video.socialUrl} target="_blank" rel="noopener noreferrer" className="ml-1 text-primary-400 underline">
                    Open original
                  </a>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 p-5">
              <h3 className="font-display text-lg font-bold text-white">{video.title}</h3>
              {video.shortDescription ? (
                <p className="mt-2 text-sm text-neutral-400">{video.shortDescription}</p>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
